import { useCallback, useEffect, useRef, useState } from "react";
import type {
  RealtimeEvent,
  TvCallSnapshot,
  TvCallView,
} from "@shared/types/foyer";
import { getBinding } from "./binding";
import { connectRealtime } from "./realtime";

/**
 * The room's live call, as the TV knows it (VC-18).
 *
 * Calls are the one realtime thing in Foyer that is NOT durable. Commands are
 * queued, acked and replayed on reconnect; a ring must not be, because a TV
 * that reconnects tomorrow replaying yesterday's `call_invite` would start
 * ringing at a resident nobody is calling - and, worse, would do it with a
 * previous resident's daughter's face on the screen.
 *
 * So this module holds two mechanisms and no more:
 *
 *  1. **A pure reducer** over the room's events ({@link reduceCallEvent}).
 *     `call_invite` starts a ring; `call_state` moves or ends the one we
 *     already hold. An event about a call we never saw an invite for is
 *     IGNORED - there is no path from a bare state event to a ringing screen,
 *     which is what stops a late-arriving `active` from painting an overlay
 *     with no caller in it.
 *
 *  2. **Reconcile on every connect** ({@link reconcileCall}), first one and
 *     every reconnect. Exactly one request, and its answer wins: `call: null`
 *     means stop ringing. This is the half that makes "no replay" safe - the
 *     TV neither misses a call that started while it was away nor keeps
 *     ringing about one that ended while it was away.
 *
 * Belt for both: a ring carries `expiresAt`, and {@link hasCallExpired} lets a
 * screen stop itself if it is never told the call is over. Forty seconds is a
 * promise the resident can hear; a socket is not a promise at all.
 */

/** Room-scoped filter: an event for another room is never ours. */
function isOurs(
  evt: Extract<RealtimeEvent, { roomId: string }>,
  roomId: string
): boolean {
  return evt.roomId === roomId;
}

/**
 * Fold one realtime event into the room's call state.
 *
 * Returns `current` **by reference** when nothing applies, so a React consumer
 * can `setState(reduceCallEvent(...))` on every event without re-rendering the
 * whole scheme for somebody else's photo upload.
 */
export function reduceCallEvent(
  current: TvCallView | null,
  evt: RealtimeEvent,
  roomId: string
): TvCallView | null {
  if (evt.type === "call_invite") {
    if (!isOurs(evt, roomId)) return current;
    // A new invite replaces whatever we held. The server enforces one live
    // session per room with a unique index, so an invite arriving means the
    // previous call is already over - trusting that is strictly safer than
    // keeping a stale ring alive next to a real one.
    return evt.call;
  }

  if (evt.type === "call_state") {
    if (!isOurs(evt, roomId)) return current;
    // Not the call we are holding (or we are holding none). Ignore rather than
    // adopt: a ring must come from an invite, which is the only event that
    // carries who is calling.
    if (!current || evt.callId !== current.callId) return current;
    if (evt.state === "ended") return null;
    if (evt.state === current.state) return current;
    return { ...current, state: evt.state };
  }

  return current;
}

/** Has the ring (or the max-duration cap) run out? A screen that is never told
 *  a call ended still stops on its own. `grace` covers clock skew between the
 *  TV and the server, which on a hospitality panel is routinely seconds. */
export function hasCallExpired(
  call: TvCallView | null,
  now: number = Date.now(),
  graceMs = 2000
): boolean {
  if (!call?.expiresAt) return false;
  const t = Date.parse(call.expiresAt);
  return Number.isFinite(t) && now > t + graceMs;
}

/** Where the reconcile answer lives. */
export function callSnapshotUrl(
  premiseId: string,
  roomId: string,
  apiBase = ""
): string {
  return `${apiBase}/api/videocall/v1/premise/${encodeURIComponent(
    premiseId
  )}/rooms/${encodeURIComponent(roomId)}/call-session`;
}

/**
 * Ask the server what is live in this room right now. `cache: "no-store"`
 * because a cached answer to this question is precisely the stale ring the
 * whole mechanism exists to prevent.
 *
 * Returns `undefined` - distinct from `null` - when the answer could not be
 * obtained, so a caller can leave its state alone rather than silently killing
 * a live call because one request failed.
 */
export async function reconcileCall(opts: {
  premiseId: string;
  roomId: string;
  screenToken?: string;
  apiBase?: string;
}): Promise<TvCallView | null | undefined> {
  if (typeof fetch !== "function") return undefined;
  const headers: Record<string, string> = {};
  if (opts.screenToken) headers["X-Screen-Token"] = opts.screenToken;
  try {
    const res = await fetch(
      callSnapshotUrl(opts.premiseId, opts.roomId, opts.apiBase),
      { headers, cache: "no-store" }
    );
    if (!res.ok) return undefined;
    const body = (await res.json()) as TvCallSnapshot;
    return body?.call ?? null;
  } catch {
    return undefined;
  }
}

export interface UseRoomCallSessionResult {
  /** The live call, or null when the room is quiet. */
  call: TvCallView | null;
  /** True once the first reconcile has answered - so a ring overlay can wait
   *  a beat rather than flashing on a reconnect. */
  ready: boolean;
  /** Drop the call locally (the TV answered/declined and the API said so).
   *  Server state remains the truth; the next reconcile corrects us. */
  clear: (callId?: string) => void;
}

/**
 * Subscribe to this room's calls: events while connected, one reconcile on
 * every (re)connect, and a self-imposed stop at `expiresAt`.
 *
 * Unbound devices and CMS previews get a permanently quiet room rather than an
 * error - there is no room whose calls they could be watching.
 */
export function useRoomCallSession(
  opts: { enabled?: boolean; apiBase?: string } = {}
): UseRoomCallSessionResult {
  const { enabled = true, apiBase } = opts;
  const [call, setCall] = useState<TvCallView | null>(null);
  const [ready, setReady] = useState(false);
  /** Bumped by every applied event. A reconcile that started BEFORE the last
   *  event lost the race and must not overwrite it: the snapshot describes the
   *  world as it was when the request was issued, the event describes it as of
   *  its own instant. Without this, a reconnect that races an incoming invite
   *  can silence a call that is genuinely ringing. */
  const eventSeq = useRef(0);

  const clear = useCallback((callId?: string) => {
    eventSeq.current += 1;
    setCall((cur) => (!cur || !callId || cur.callId === callId ? null : cur));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const binding = getBinding();
    if (!binding) {
      setReady(true);
      return;
    }
    const { premiseId, roomId, screenToken } = binding;
    let cancelled = false;

    const reconcile = async () => {
      const seenAtStart = eventSeq.current;
      const snapshot = await reconcileCall({
        premiseId,
        roomId,
        screenToken,
        apiBase,
      });
      if (cancelled) return;
      setReady(true);
      if (snapshot === undefined) return; // unreachable server - hold state
      if (eventSeq.current !== seenAtStart) return; // an event overtook us
      setCall(snapshot);
    };

    const close = connectRealtime({
      premiseId,
      roomId,
      screenToken,
      apiBase,
      onEvent: (evt) => {
        if (evt.type !== "call_invite" && evt.type !== "call_state") return;
        eventSeq.current += 1;
        setCall((cur) => reduceCallEvent(cur, evt, roomId));
      },
      // Fires on the first connect AND on every reconnect - which is the whole
      // point: the gap we could not observe is closed by asking, once.
      onOpen: () => void reconcile(),
    });

    // The transport may have been up before we mounted (or may never report an
    // open, as with a stubbed EventSource), so ask once regardless.
    void reconcile();

    // FCM wake (VC-28): when the native shell foregrounds Foyer for an inbound
    // call, it dispatches `foyer:incoming-call`. The realtime socket will
    // reconnect and `onOpen` reconcile on its own, but that can lag a backgrounded
    // WebView resuming from Doze by seconds the resident would hear as silence -
    // so we reconcile the instant the shell tells us a call is waiting. Idempotent
    // with the socket path: whichever answers first wins, the other is a no-op.
    const onIncomingCall = () => void reconcile();
    if (typeof window !== "undefined") {
      window.addEventListener("foyer:incoming-call", onIncomingCall);
    }

    return () => {
      cancelled = true;
      close();
      if (typeof window !== "undefined") {
        window.removeEventListener("foyer:incoming-call", onIncomingCall);
      }
    };
  }, [enabled, apiBase]);

  // Self-stop. A ring that outlives its deadline is a ring nobody can answer.
  useEffect(() => {
    if (!call?.expiresAt) return;
    const due = Date.parse(call.expiresAt);
    if (!Number.isFinite(due)) return;
    const id = setTimeout(
      () => setCall((cur) => (cur?.callId === call.callId ? null : cur)),
      Math.max(0, due + 2000 - Date.now())
    );
    return () => clearTimeout(id);
  }, [call]);

  return { call, ready, clear };
}
